import { Navigate, Route, Routes } from 'react-router-dom'

import HoopsPanel from './HoopsPanel.jsx'
import LeadersView from './LeadersView.jsx'
import PlayerView from './PlayerView.jsx'
import PlayersView from './PlayersView.jsx'
import SeasonShell from './SeasonShell.jsx'
import StandingsView from './StandingsView.jsx'
import TeamView from './TeamView.jsx'
import TeamsView from './TeamsView.jsx'
import TierBadge from '../../components/TierBadge.jsx'
import { sectionList } from '../../content.js'
import NotFound from '../../routes/NotFound.jsx'
import { useDocumentTitle } from '../../useDocumentTitle.js'
import { TABS } from './paths.js'
import { useScrollToTopOnPush } from './scroll.js'

// Everything beneath `/hoops`. P6-329c (GN-2c).
//
// ═══════════════════════════════════════════════════════════════════════════
// ONE SPLAT ROUTE IN App.jsx, AND THE TABLE BELOW IS THE WHOLE OF THE HOOPS PATHS
// ═══════════════════════════════════════════════════════════════════════════
//
// `App.jsx` registers `/hoops/*` once (a section's own path, `/hoops`, is a real route and redirects to Tonight); this
// is what answers beneath it. The paths are in `paths.js` and the guard is the registry's: `content-src/sections.json`
// has a `hoops` entry (tier 3, a child of the portal so it is off the nav), and `functions/_lib/guard.js` matches
// "exact match, or anything beneath it", which is what makes `/hoops/players/1629029` as guarded as `/trips/42` and
// `/watch/archive` are. src/test/portal/hoops-routes.test.jsx drives the REAL `<App/>` at every spelling the router
// accepts and asks `isGuardedPath` about each one (CLAUDE.md gotcha 21).
//
// ANYTHING ELSE UNDER `/hoops` IS THE SITE'S NOT-FOUND, not a quiet redirect to Tonight: a mistyped link should say
// so. (A guarded 404 is behind the sign-in like everything else here, which is the safe direction.)

export const HOOPS_ROUTE_PATTERNS = Object.freeze([
  'tonight',
  'players',
  'players/:personId',
  'leaders',
  'standings',
  'teams',
  'teams/:teamId',
])

/** The Tonight route's page: the portal's own panel (the board, and a game as a history entry) on a page of its own. */
function TonightPage({ docPath, tier, timeZone }) {
  useDocumentTitle('Hoops')
  useScrollToTopOnPush()
  return (
    <article className="d2d-page d2d-hoopspage" data-testid="hoops-page">
      {/* The panel's own headline is an h2 (inside the portal the page's h1 is the member's name), so a page of its own
          needs the h1 every page has. It says what the page is, to a reader who cannot see the tabs. */}
      <h1 className="d2d-sr-only">Hoops {'·'} Tonight</h1>
      <HoopsPanel docPath={docPath} timeZone={timeZone} />
      <div className="d2d-hoops__foot">
        <TierBadge tier={tier} />
      </div>
    </article>
  )
}

/** `/hoops` with nothing after it: a section's own path is a real route, and the first tab is where it goes. */
export function HoopsIndex() {
  return <Navigate to={TABS[0].path} replace />
}

export default function HoopsRoutes({ section, timeZone } = {}) {
  const docPath = sectionList.find((entry) => entry.id === 'docs-hoops')?.path ?? null
  // The tier is the registry's (`content-src/sections.json`, tier 3), as `TripDates` reads it: a page wears the tier it
  // is declared at, so it cannot be promoted by accident.
  const tier = section?.tier ?? 3
  return (
    <Routes>
      <Route path="tonight" element={<TonightPage docPath={docPath} tier={tier} timeZone={timeZone} />} />
      <Route element={<SeasonShell docPath={docPath} tier={tier} timeZone={timeZone} />}>
        <Route path="players" element={<PlayersView />} />
        <Route path="players/:personId" element={<PlayerView />} />
        <Route path="leaders" element={<LeadersView />} />
        <Route path="standings" element={<StandingsView />} />
        <Route path="teams" element={<TeamsView />} />
        <Route path="teams/:teamId" element={<TeamView />} />
      </Route>
      <Route path="*" element={<NotFound />} />
    </Routes>
  )
}
